import type { TranscriptMessage, TranscriptToolCall } from "@/types";
import { cn } from "@/utils";

interface TranscriptViewProps {
  messages: TranscriptMessage[];
}

// Asepha-voice writes a flat utterance stream:
//   {speaker: "user"|"assistant", text, is_final, timestamp}
// Vapi writes OpenAI-formatted chat messages:
//   {role, content, tool_calls?, tool_call_id?}
// Normalize the asepha-voice shape into the vapi shape so the renderer below
// only has to handle one format. Drop interim STT entries (is_final === false).
function normalizeMessages(raw: TranscriptMessage[]): TranscriptMessage[] {
  return raw.reduce<TranscriptMessage[]>((acc, m) => {
    const anyM = m as TranscriptMessage & {
      speaker?: string;
      text?: string;
      is_final?: boolean;
    };
    if (!m.role && anyM.speaker) {
      if (anyM.is_final === false) return acc;
      acc.push({
        role: anyM.speaker,
        content: anyM.text ?? "",
      });
      return acc;
    }
    acc.push(m);
    return acc;
  }, []);
}

function formatToolArguments(raw: string): string {
  const s = (raw || "").trim();
  if (!s) return "";
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

function ToolCallsBlock({
  tool_calls,
  variant,
}: {
  tool_calls: TranscriptToolCall[];
  variant: "agent" | "neutral";
}) {
  return (
    <div
      className={cn(
        "mt-2 space-y-2 rounded-md border p-2.5 font-mono text-xs",
        variant === "agent"
          ? "border-primary-foreground/25 bg-primary-foreground/10"
          : "border-border bg-background/80"
      )}
    >
      {tool_calls.map((tc, i) => {
        const name = tc.function?.name ?? "tool";
        const args = formatToolArguments(tc.function?.arguments ?? "");
        return (
          <div key={tc.id ?? `tc-${i}`}>
            <div className="font-semibold text-[11px] uppercase tracking-wide opacity-80">
              Tool · {name}
            </div>
            {args ? (
              <pre className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed opacity-95">
                {args}
              </pre>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function speechText(msg: TranscriptMessage): string {
  const raw = (msg.message ?? msg.content ?? "") as string;
  if (typeof raw !== "string") return "";
  return raw;
}

function isBlankSpeech(text: string): boolean {
  return text.replace(/\s/g, "").length === 0;
}

export function TranscriptView({ messages }: TranscriptViewProps) {
  const visible = normalizeMessages(messages).filter((m) => m.role !== "system");

  if (visible.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        No transcript available
      </p>
    );
  }

  return (
    <div className="max-h-[min(70vh,720px)] min-h-[120px] overflow-y-auto overflow-x-hidden rounded-md border bg-muted/20 p-3">
      <div className="flex flex-col gap-3">
        {visible.map((msg, i) => {
          const role = (msg.role || "").toLowerCase();
          const text = speechText(msg);
          const hasSpeech = !isBlankSpeech(text);
          const hasTools = Boolean(msg.tool_calls?.length);

          if (role === "tool") {
            const body = hasSpeech ? text : "(empty)";
            return (
              <div key={`${i}-tool-${msg.tool_call_id ?? ""}`} className="flex justify-center">
                <div className="max-w-[95%] rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-foreground">
                  <span className="mb-1 block text-xs font-medium text-amber-900 dark:text-amber-200">
                    Tool result
                    {msg.tool_call_id ? (
                      <span className="ml-1 font-mono text-[10px] opacity-70">
                        {msg.tool_call_id.length > 16
                          ? `${msg.tool_call_id.slice(0, 14)}…`
                          : msg.tool_call_id}
                      </span>
                    ) : null}
                  </span>
                  <p className="whitespace-pre-wrap break-words leading-relaxed">{body}</p>
                </div>
              </div>
            );
          }

          const isAgent = role === "assistant";
          const isOtherParty = role === "user" || role === "bot";

          if (!isAgent && !isOtherParty) {
            return (
              <div key={`${i}-unknown`} className="flex justify-start">
                <div className="max-w-[95%] rounded-lg border bg-muted px-3 py-2 text-sm">
                  <span className="mb-1 block text-xs font-medium capitalize text-muted-foreground">
                    {role || "message"}
                  </span>
                  {hasSpeech ? (
                    <p className="whitespace-pre-wrap break-words leading-relaxed">{text}</p>
                  ) : null}
                  {hasTools ? (
                    <ToolCallsBlock tool_calls={msg.tool_calls!} variant="neutral" />
                  ) : null}
                </div>
              </div>
            );
          }

          return (
            <div
              key={`${i}-${role}-${hasTools ? "tc" : "t"}`}
              className={cn("flex", isAgent ? "justify-end" : "justify-start")}
            >
              <div
                className={cn(
                  "max-w-[min(92%,42rem)] rounded-lg px-3 py-2 text-sm shadow-sm",
                  isAgent
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                )}
              >
                <span
                  className={cn(
                    "mb-1 block text-xs font-medium opacity-80",
                    isAgent ? "" : "text-muted-foreground"
                  )}
                >
                  {isAgent ? "Agent" : "Insurance line"}
                </span>
                {hasSpeech ? (
                  <p className="whitespace-pre-wrap break-words leading-relaxed">{text}</p>
                ) : null}
                {hasTools ? (
                  <ToolCallsBlock
                    tool_calls={msg.tool_calls!}
                    variant={isAgent ? "agent" : "neutral"}
                  />
                ) : null}
                {!hasSpeech && !hasTools ? (
                  <p className="text-xs italic opacity-70">(no content)</p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
